import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { btn } from "@/components/ds/styles";
import { formatUsdShort } from "@/lib/payments/currency";
import { publicSiteSettingsQuery } from "@/routes/_public/route";
import { supportWhatsAppUrl } from "@/lib/social";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { TraumaPreviewBanner } from "@/components/trauma/preview-banner";
import { TraumaLocalReceiptForm } from "@/components/trauma/buy-local-form";
import { PaypalReceiptForm, type PaypalSubmitPayload } from "@/components/payments/paypal-receipt-form";
import { InsideSyriaPaymentInstructions, PaymentRegionSelector, PaymentStepHeading } from "@/components/payments/payment-flow";
import { getTraumaBuyContext, getTraumaReceiptUploadUrl, submitTraumaPurchase } from "@/lib/trauma/purchase.functions";
import { getTraumaGate } from "@/lib/trauma/gate.functions";
import { isTraumaCourseId } from "@/lib/trauma/ids";
import { traumaBuyDestination, traumaOwnedState } from "@/lib/trauma/buy-ui";
import { useTraumaPurchaseState } from "@/lib/trauma/purchase-query";
import { TRAUMA_BUY_UI as UI } from "@/lib/trauma/ui-copy";
import { PAYMENT_COPY } from "@/lib/payments/payment-copy";
import { SMART_CHECKOUT_COPY } from "@/lib/bousala/smart-buyer-copy";
import { formatSypPrice } from "@/lib/payments/syp";

const buyQuery = (productSlug: string, viewer: string | null = null) => queryOptions({
  queryKey: ["trauma", "buy-context", productSlug, viewer],
  queryFn: () => getTraumaBuyContext({ data: { productSlug } }),
  staleTime: 0, gcTime: 0, retry: false,
});

export const Route = createFileRoute("/_public/courses/trauma/buy/$productSlug")({
  loader: async ({ context, params }) => {
    if (params.productSlug !== "trauma-bundle" && !isTraumaCourseId(params.productSlug.slice(7))) throw notFound();
    if (!params.productSlug.startsWith("trauma-")) throw notFound();
    // Like the catalog/Smart preview: SSR has no bearer; browser calls verify the admin on the server.
    const gate = await getTraumaGate();
    if (!gate.canView) throw redirect({ to: "/", statusCode: 302 });
    const result = await context.queryClient.fetchQuery(buyQuery(params.productSlug));
    const destination = traumaBuyDestination(params.productSlug, Boolean(result.product));
    if (destination?.kind === "course") throw redirect({ to: "/courses/trauma/$id", params: { id: destination.id }, statusCode: 302 });
    if (destination?.kind === "catalog") throw redirect({ to: "/courses", statusCode: 302 });
    return result;
  },
  head: ({ loaderData }) => {
    const title = `${loaderData?.product?.title_ar ?? UI.buyCourse} — د. سارة النحّاس`;
    const description = "طلب شراء دورة صدمات الطفولة وإرسال إثبات الدفع للمراجعة.";
    return { meta: [{ title }, { name: "description", content: description }, { property: "og:title", content: title }, { property: "og:description", content: description }, { property: "og:type", content: "website" }, { name: "twitter:card", content: "summary" }, { name: "robots", content: "noindex, nofollow" }] };
  },
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: TraumaBuyPage,
});

function TraumaBuyPage() {
  const { productSlug } = Route.useParams();
  return <TraumaBuyContent key={productSlug} />;
}

function TraumaBuyContent() {
  const initialData = Route.useLoaderData();
  const { productSlug } = Route.useParams();
  const { session, gate } = useTraumaPurchaseState();
  const queryClient = useQueryClient();
  const query = buyQuery(productSlug, session.userId);
  if (queryClient.getQueryData(query.queryKey) === undefined && !session.ready) queryClient.setQueryData(query.queryKey, initialData);
  const { data = initialData } = useQuery({ ...query, enabled: session.ready });
  const upload = useServerFn(getTraumaReceiptUploadUrl);
  const send = useServerFn(submitTraumaPurchase);
  const [region, setRegion] = useState<"outside" | "SY">("outside");
  const [received, setReceived] = useState(false);
  const [alreadyGranted, setAlreadyGranted] = useState(false);
  const { data: settings } = useQuery(publicSiteSettingsQuery());
  const whatsapp = settings?.clinic_whatsapp?.trim() ? supportWhatsAppUrl(settings.clinic_whatsapp, "مرحباً، عندي سؤال عن شراء دورة") : "";
  const product = data.product;
  if (!product) return null;
  const lockedEmail = session.userId && gate && !gate.isAdmin ? session.email : undefined;
  const owned = traumaOwnedState(data.alreadyOwned, alreadyGranted);
  const getUploadUrl = (filename: string) => upload({ data: { filename } });
  const submit = async (payload: PaypalSubmitPayload) => {
    const result = await send({ data: { ...payload, productSlug } });
    setAlreadyGranted(result.alreadyGranted);
    await queryClient.invalidateQueries({ queryKey: ["trauma", "owned-course-ids"] });
    return result;
  };
  const primaryBtn = `${btn.primary} mt-5`;
  return (
    <main dir="rtl" className="min-h-dvh bg-cream-200 font-body text-[17px] leading-[1.85] text-text-primary">
      <div className="mx-auto max-w-2xl px-5 py-10 lg:py-16">
        <TraumaPreviewBanner show={data.isPreview} />
        <h1 className="font-display text-[34px] font-extrabold leading-[1.3] lg:text-[42px]">شراء <mark className="mark bg-transparent text-inherit">الدورة</mark></h1>
        <section className="mt-6 rounded-ds-xl bg-cream-100 p-6">
          <p className="font-display text-[17px] font-bold">{product.title_ar}</p>
          <p className="mt-2 font-display text-[30px] font-extrabold leading-none text-amber-800"><span dir="ltr">{formatUsdShort(product.price_usd)}</span></p>
          {region === "SY" ? <p className="mt-2 text-[15px] font-bold text-brown-500">{formatSypPrice(product.price_usd)} {SMART_CHECKOUT_COPY.ar.sypCurrency}</p> : null}
        </section>
        <div className="mt-8">
          {owned ? (
            <div className="rounded-ds-2xl bg-green-100 p-7">
              <p className="font-display text-[23px] font-extrabold text-green-800">{productSlug === "trauma-bundle" ? UI.ownedBundle : UI.ownedCourse}</p>
              <Link to="/portal/courses" className={primaryBtn}>{UI.myCourses} ←</Link>
            </div>
          ) : received ? (
            <div role="status" className="rounded-ds-2xl bg-green-100 p-7">
              <p className="font-display text-[23px] font-extrabold text-green-800">{PAYMENT_COPY.ar.received}</p>
              <p className="mt-3 text-[17px] text-text-primary">{UI.review}</p>
              {whatsapp ? <a href={whatsapp} target="_blank" rel="noreferrer" className="mt-5 inline-flex min-h-12 items-center rounded-ds-pill border border-hairline-default bg-surface px-6 font-display text-[16px] font-bold text-brown-700">المراسلة على واتساب ←</a> : null}
            </div>
          ) : !session.ready || !gate?.canView ? <div aria-busy="true" className="h-48 animate-pulse rounded-ds-xl bg-cream-100 motion-reduce:animate-none" /> : <>
            <PaymentRegionSelector value={region} onChange={setRegion} outsideLabel={SMART_CHECKOUT_COPY.ar.outsideSyria} insideLabel={SMART_CHECKOUT_COPY.ar.insideSyria} />
            {region === "outside" ? <PaypalReceiptForm key={`${session.userId ?? "guest"}-${lockedEmail ?? "editable"}`} idPrefix="trauma-paypal" priceUsd={product.price_usd} instructions={data.paypal_outside_syria} lockedEmail={lockedEmail} getUploadUrl={getUploadUrl} submit={submit} onDone={() => setReceived(true)} footerNote={UI.review} /> : <>
              {data.inside_syria?.body_md ? <section><PaymentStepHeading number={1}>{PAYMENT_COPY.ar.payAmount}</PaymentStepHeading><InsideSyriaPaymentInstructions markdown={data.inside_syria.body_md} title={data.inside_syria.title_ar} /></section> : <p className="font-bold text-coral-700">{PAYMENT_COPY.ar.unavailable}</p>}
              <TraumaLocalReceiptForm key={`${session.userId ?? "guest"}-${lockedEmail ?? "editable"}`} lockedEmail={lockedEmail} getUploadUrl={getUploadUrl} submit={submit} onDone={() => setReceived(true)} />
            </>}
          </>}
        </div>
      </div>
    </main>
  );
}
